import React, { Fragment, useEffect } from "react";

// ASSETS

// STYLES

import {
  StrapiMarkup,
  StrapiTreeTextContainer,
  StrapiTreeTextLabel,
  Img,
} from "./StrapiTreeTextStyled";

// LIBRARIES

import DOMPurify from "dompurify";
import { useSelector } from "react-redux";

// MISC
import {
  IProps,
  TextKerning,
  TextSize,
  TextSpacing,
} from "./StrapiTreeTextModel";
import { TextType } from "components/Atoms/StrapiText/StrapiTextModel";
import { marked } from "marked";

// REDUX

import {
  accessibilityBiggerLetterSpacingSelector,
  accessibilityBiggerLineHeightSelector,
  accessibilityBiggerTextSelector,
} from "slices/UISlice/uiSliceSelectors";

//COMPONENTS

import StrapiText from "components/Atoms/StrapiText/StrapiText";
import CustomText from "components/Atoms/CustomText/CustomText";

// COMPONENTS
const TextSpeller = (props: any) => {
  const { text, wait } = props;

  useEffect(() => {
    let counter = 1;
    const intervalId = setInterval(() => {
      setSpell(() => text.slice(0, counter));
      counter++;
      if (counter % 32 === 0) {
        const elem = document.body;
        elem.scrollTop = elem.scrollHeight;
      }
      if (counter > text.length) {
        const elem = document.body;
        elem.scrollTop = elem.scrollHeight;
        document
          .getElementById("chat-box")
          .querySelectorAll("a")
          .forEach((el) => {
            el.target = "_blank";
          });
        clearInterval(intervalId);
      }
    }, wait);
  }, [text, wait]);

  const [spell, setSpell] = React.useState<any>("");

  return (
    <Fragment>
      <StrapiMarkup dangerouslySetInnerHTML={{ __html: spell }} />
    </Fragment>
  );
};

const checkLink = (child: any, index) => {
  // eslint-disable-next-line
  const regex: RegExp =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\(?:[^\/\n\s]+\/\S+\/|(?:v|embed|watch)\?(?:(?:\S+&)?v(?:i)?=|\S*[?&]?(?:vi?=))|youtu\.be\/)([a-zA-Z0-9_-]+)/;

  const match = child.url.match(regex);

  if (match) {
    return (
      <div className={"youtube"} key={index}>
        <iframe
          title={"youtube"}
          style={{ width: "100%" }}
          height="315"
          src={`https://www.youtube.com/embed/${match[1]}`}
          allowFullScreen
        ></iframe>
        {/* <div key={`${child.id}-${index}`}><a href={child.url} className={"guideLink"} */}
        {/* rel={"noreferrer"} target={"_blank"}>{renderChildren(child.children)}</a></div> */}
      </div>
    );
  } else {
    return (
      <a
        key={index}
        href={child.url}
        className={"guideLink"}
        rel={"noreferrer"}
        target={"_blank"}
      >
        {renderChildren(child.children)}
      </a>
    );
  }
};

const createMarkup = (markdown: string): string => {
  const renderer = new marked.Renderer();

  renderer.link = function (href, title, text) {
    return `<a href="${href}" target="_blank" title="${text}">${text}</a>`;
  };

  try {
    // Use marked.parse() which is synchronous by default in marked v12+
    const rawMarkup = marked.parse(markdown, {
      renderer: renderer,
    });

    // Handle both string and Promise<string> return types
    if (rawMarkup instanceof Promise) {
      console.warn('marked.parse() returned a Promise. Consider using an async/await pattern for better handling.');
      return 'Loading...';
    }

    // Ensure we're working with a string
    const markupString = String(rawMarkup);
    const sanitizedMarkup = DOMPurify.sanitize(markupString);
    return sanitizedMarkup;
  } catch (error) {
    console.error('Error processing markdown:', error);
    return 'Error loading content';
  }
};

const renderText = (children: any) => {
  return children.map((child, index: number) => {
    if (child.type === "text") {
      return child.text;
    }

    return renderText(child.children);
  });
};
const renderChildren = (children: any[], spell = 0, useMarkup = false, color?: string) => {
  const Heading = ({ level, children }) => {
    return <StrapiText color={color} type={TextType[`H${level}`]}>{children}</StrapiText>;
  };

  const applyMarkup = useMarkup;

  return children.map((child, index: number) => {
    switch (child.type) {
      case "link":
        return checkLink(child, index);
      case "image":
        return (
          <Fragment key={index}>
            <Img key={index} src={child?.image?.url} alt={"pht-extern"} loading="lazy" decoding="async"/>
            <>{renderChildren(child.children, spell, applyMarkup)}</>
          </Fragment>
        );
      case "paragraph":
        return (
          <StrapiText color={color} type={TextType.P1} key={index}>
            {renderChildren(child.children, spell, applyMarkup)}
          </StrapiText>
        );
      case "list-item":
        return (
          <StrapiText color={color} type={TextType.LI} key={index}>
            {renderChildren(child.children, spell, applyMarkup)}
          </StrapiText>
        );
      case "heading":
        return (
          <Heading key={index} level={child.level}>
            {renderChildren(child.children, spell, applyMarkup)}
          </Heading>
        );
      case "list":
        if (child.format === "unordered") {
          return (
            <StrapiText color={color} key={index} type={TextType.UL}>
              {renderChildren(child.children, spell, applyMarkup)}
            </StrapiText>
          );
        } else {
          return (
            <StrapiText color={color} key={index} type={TextType.OL}>
              {renderChildren(child.children, spell, applyMarkup)}
            </StrapiText>
          );
        }

      case "text":
        return child.bold ? (
          <strong key={index}>{child.text}</strong>
        ) : (
          <Fragment key={index}>
            {spell > 0 ? (
              <TextSpeller wait={spell} text={createMarkup(child.text)} />
            ) : applyMarkup ? (
              <StrapiMarkup
                dangerouslySetInnerHTML={{ __html: createMarkup(child.text) }}
              />
            ) : (
              <>{child.text}</>
            )}
          </Fragment>
        );
      default:
        return "";
    }
  });
};

// CONFIGURATION
const StrapiTreeText = (props: IProps) => {
  // PROPERTIES
  const {
    type,
    text,
    size = TextSize.default,
    color,
    rowSpacing = TextSpacing.default,
    kerning = TextKerning.default,
    spell = 0,
    textOnly = false,
    useMarkup = false,
  } = props;

  const getHtmlTag = (type) => {
    if (typeof type === "string") {
      if (type.startsWith("H")) {
        return `h${type.substring(1)}`;
      } else if (type.startsWith("P")) {
        return "p";
      }
    }
    return "span";
  };

  const Tag = getHtmlTag(type);

  // API REQUESTS

  // LIBRARY CONSTANTS

  // STATE CONSTANTS
  const accessibilityBiggerText: boolean = useSelector(
    accessibilityBiggerTextSelector
  );
  const accessibilityBiggerLineHeight: boolean = useSelector(
    accessibilityBiggerLineHeightSelector
  );
  const accessibilityBiggerLetterSpacing: boolean = useSelector(
    accessibilityBiggerLetterSpacingSelector
  );

  // LIFE CYCLE

  // EVENT HANDLERS

  return (
    <StrapiTreeTextContainer>
      {text && (
        <StrapiTreeTextLabel
          as={Tag}
          type={type}
          size={size}
          rowSpacing={rowSpacing}
          kerning={kerning}
          color={color}
          accessibilityBiggerText={accessibilityBiggerText}
          accessibilityBiggerLineHeight={accessibilityBiggerLineHeight}
          accessibilityBiggerLetterSpacing={accessibilityBiggerLetterSpacing}
        >
          {textOnly ? (
            <CustomText text={renderText(text)} color={color} />
          ) : (
            renderChildren(text, spell, useMarkup, color)
          )}
        </StrapiTreeTextLabel>
      )}
    </StrapiTreeTextContainer>
  );
};
export default StrapiTreeText;
